import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type CardTemplatePermissionDb = {
  cardTemplateId: string;
  feeCharging: boolean;
  designCardPermission: boolean;
  overwriteCardTemplatePermission: boolean;
  saveNewCardTemplatePermission: boolean;
}
const initial: CardTemplatePermissionDb = {
  cardTemplateId: '',
  feeCharging: false,
  designCardPermission: false,
  overwriteCardTemplatePermission: false,
  saveNewCardTemplatePermission: false
};

export type CardTemplatePermissionDbHashType = {
  [key in string]: CardTemplatePermissionDb;
}

export type CardTemplatePermissionAddPayload = {
  id?: string;
} & CardTemplatePermissionDb;
export type CardTemplatePermissionUpdatePayload = {
  id: string;
} & { [P in keyof CardTemplatePermissionDb]?: CardTemplatePermissionDb[P] };

export type CardTemplatePermissionDbState = {
  cardTemplatePermissions: CardTemplatePermissionDbHashType;
}

export const generateCardTemplatePermissionId = (): string => generateId('ctp');
let initialState: CardTemplatePermissionDbState = { cardTemplatePermissions: {} };
const key = 'cardTemplatePermissions';

const cardTemplatePermissionDbSlice = createSlice({
  name: 'cardTemplatePermissionDb',
  initialState,
  reducers: {
    initializeCardTemplatePermissions(state: CardTemplatePermissionDbState) {
      state.cardTemplatePermissions = {};
    },
    addCardTemplatePermission(state: CardTemplatePermissionDbState, action: PayloadAction<CardTemplatePermissionAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateCardTemplatePermissionId();
      state.cardTemplatePermissions[id] = obj;
      saveData(key, id, obj);
    },
    updateCardTemplatePermission(state: CardTemplatePermissionDbState, action: PayloadAction<CardTemplatePermissionUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.cardTemplatePermissions[id] === undefined) {
        console.log(`*** CardTemplatePermission not found: ${id}`);
      } else {
        const newData = { ...state.cardTemplatePermissions[id], ...obj };
        state.cardTemplatePermissions[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteCardTemplatePermission(state: CardTemplatePermissionDbState, action: PayloadAction<CardTemplatePermissionUpdatePayload>) {
      const id = action.payload.id;
      delete state.cardTemplatePermissions[id];
      deleteData(key, id);
    },
    saveCardTemplatePermissions(state: CardTemplatePermissionDbState) {
      saveAllData(key, state.cardTemplatePermissions);
    },
    signUpCardTemplatePermissions(state: CardTemplatePermissionDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.cardTemplatePermissions, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: CardTemplatePermissionDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.cardTemplatePermissions = updatedValues;
      if (changed) saveAllData(key, state.cardTemplatePermissions);
    })
  }
})

export const { initializeCardTemplatePermissions, addCardTemplatePermission, updateCardTemplatePermission, deleteCardTemplatePermission, saveCardTemplatePermissions, signUpCardTemplatePermissions } = cardTemplatePermissionDbSlice.actions;
export default cardTemplatePermissionDbSlice.reducer;
